import { useEffect, useState } from "react";
import { Bookmark, ChevronRight, Star } from "lucide-react";

import {
  bewaarAnalyse,
  lijstAnalyses,
  verwijderAnalyse,
  type AnalyseStand,
  type BewaardeAnalyse,
} from "../api/insights";

/**
 * Bewaarde analyses in de zijkolom (DI-007).
 *
 * <p>Wat je bewaart is de definitie, niet de uitkomst. Openen betekent dus: opnieuw uitrekenen op de
 * gegevens van vandaag. Een bewaarde "instroom per maand" toont volgende maand de nieuwe maand, en
 * dat is precies waarom hier nergens een getal wordt opgeslagen of getoond.
 *
 * <p>Het openen gaat via het opgeslagen plan en niet door de vraag opnieuw in de zoekbalk te zetten.
 * Dat laatste zou er hetzelfde uitzien en iets anders doen: de vraag zou opnieuw worden vertaald, en
 * als de vertaalregels intussen zijn veranderd geeft dezelfde bewaarde analyse andere cijfers onder
 * dezelfde naam.
 */
export function BewaardeAnalyses({
  huidigeVraag,
  huidigeStand,
  kanBewaren,
  onOpenen,
  snelkoppelingen = [],
  onSnel,
  t,
}: {
  /** De vraag die nu op het scherm staat; die wordt bewaard. */
  huidigeVraag: string;
  /**
   * De schermstand van de standaardanalyse die nu op het scherm staat.
   *
   * <p>Bij een getypte vraag leeg. Staat hij erin, dan gaat hij mee naar de server en bouwt die het
   * plan uit zijn eigen catalogus — zodat een bewaarde analyse nooit iets anders kan zijn dan wat
   * het scherm ook laat zien.
   */
  huidigeStand?: AnalyseStand;
  /** Alleen bewaren wat ook echt een analyse opleverde — anders bewaar je een lege belofte. */
  kanBewaren: boolean;
  onOpenen: (id: string, stand: AnalyseStand | null) => void;
  /**
   * Vaste onderdelen om snel te openen.
   *
   * <p>Dezelfde onderdelen als in de keuzelijst, alleen sneller bereikbaar. Een eigen naam hier zou
   * naast de catalogus gaan lopen zodra iemand daar iets bijstelt, dus die komt mee van boven.
   */
  snelkoppelingen?: { id: string; naam: string; uitleg: string }[];
  onSnel?: (id: string) => void;
  t: (key: string) => string;
}) {
  // Alleen tabs als er ook een tweede lijst is om naar over te schakelen.
  const metTabs = snelkoppelingen.length > 0;
  const [zijtab, setZijtab] = useState<"SNEL" | "BEWAARD">("SNEL");
  const [lijst, setLijst] = useState<BewaardeAnalyse[] | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [bezigMetBewaren, setBezigMetBewaren] = useState(false);
  const [naam, setNaam] = useState("");
  const [zichtbaarheid, setZichtbaarheid] = useState("PRIVE");

  const laden = () => {
    lijstAnalyses()
      .then(setLijst)
      .catch(() => setLijst([]));
  };
  useEffect(laden, []);

  const bewaar = async () => {
    setFout(null);
    try {
      await bewaarAnalyse({ naam, vraag: huidigeVraag, zichtbaarheid, stand: huidigeStand });
      setNaam("");
      setBezigMetBewaren(false);
      laden();
    } catch (e) {
      // De melding van de server tonen en niet vervangen: die weet wélke reden er is (geen naam,
      // geen vestiging gekozen bij delen, geen analyse in deze vraag).
      setFout(e instanceof Error ? e.message : t("insights.foutOnbekend"));
    }
  };

  const gooiWeg = async (id: string) => {
    try {
      await verwijderAnalyse(id);
      laden();
    } catch (e) {
      setFout(e instanceof Error ? e.message : t("insights.foutOnbekend"));
    }
  };

  return (
    <section className="fawkes-insights">
      {/* Twee lijsten met twee betekenissen: veelgebruikt is wat er altijd is, bewaard is wat jij
          hebt opgeslagen. Ze door elkaar zetten zou je laten zoeken welke van de twee je voor je
          hebt — en alleen die tweede kun je weggooien.

          Zonder snelkoppelingen is er niets te kiezen en staat er alleen de kop. */}
      {metTabs ? (
      <div className="fawkes-insights__tabs" role="tablist" aria-label={t("insights.kop")}>
        <button
          type="button" role="tab"
          aria-selected={zijtab === "SNEL"}
          className={"fawkes-insights__tab"
            + (zijtab === "SNEL" ? " fawkes-insights__tab--aan" : "")}
          onClick={() => setZijtab("SNEL")}
        >
          <Star aria-hidden="true" />
          {t("insights.veelgebruikt")}
        </button>
        <button
          type="button" role="tab"
          aria-selected={zijtab === "BEWAARD"}
          className={"fawkes-insights__tab"
            + (zijtab === "BEWAARD" ? " fawkes-insights__tab--aan" : "")}
          onClick={() => setZijtab("BEWAARD")}
        >
          <Bookmark aria-hidden="true" />
          {t("insights.kop")}
        </button>
      </div>
      ) : (
        <h3 className="fawkes-insights__kop">
          <Bookmark aria-hidden="true" />
          {t("insights.kop")}
        </h3>
      )}

      {metTabs && zijtab === "SNEL" ? (
        snelkoppelingen.length === 0 ? (
          <p className="fawkes-insights__leeg">{t("insights.geenSnelkoppelingen")}</p>
        ) : (
          <ul className="fawkes-insights__lijst">
            {snelkoppelingen.map((k) => (
              <li className="fawkes-insights__item" key={k.id}>
                <button type="button" className="fawkes-insights__snel"
                  onClick={() => onSnel?.(k.id)}>
                  <span className="fawkes-insights__snel-tekst">
                    <span className="fawkes-insights__snel-naam">{k.naam}</span>
                    <span className="fawkes-insights__snel-sub">{k.uitleg}</span>
                  </span>
                  <ChevronRight aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )
      ) : (
      <>

      {lijst === null ? null : lijst.length === 0 ? (
        <p className="fawkes-insights__leeg">{t("insights.nogGeen")}</p>
      ) : (
        <ul className="fawkes-insights__lijst">
          {lijst.map((a) => (
            <li className="fawkes-insights__item" key={a.id}>
              <button
                type="button"
                className="fawkes-insights__openen"
                onClick={() => onOpenen(a.id, a.stand)}
                // De vraag als tooltip bij een getypte analyse; bij een standaardanalyse is die
                // leeg en zegt de naam het al.
                title={a.vraag || a.naam}
              >
                {a.naam}
              </button>
              {/* Alleen de eigenaar mag weggooien; delen geeft leesrecht, geen zeggenschap. */}
              {a.vanMij && (
                <button
                  type="button"
                  className="fawkes-insights__weg"
                  onClick={() => void gooiWeg(a.id)}
                  aria-label={`${t("insights.verwijderen")}: ${a.naam}`}
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {kanBewaren && !bezigMetBewaren && (
        <button
          type="button"
          className="fui-button fui-button--secondary fawkes-insights__bewaarknop"
          onClick={() => setBezigMetBewaren(true)}
        >
          {t("insights.bewaren")}
        </button>
      )}

      {bezigMetBewaren && (
        <div className="fawkes-insights__formulier">
          <input
            className="fawkes-insights__naam"
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
            placeholder={t("insights.naamPlaceholder")}
            aria-label={t("insights.naam")}
          />
          <select
            className="fawkes-insights__zicht"
            value={zichtbaarheid}
            onChange={(e) => setZichtbaarheid(e.target.value)}
            aria-label={t("insights.zichtbaarheid")}
          >
            <option value="PRIVE">{t("insights.zicht.prive")}</option>
            <option value="VESTIGING">{t("insights.zicht.vestiging")}</option>
            <option value="ORGANISATIE">{t("insights.zicht.organisatie")}</option>
          </select>
          <div className="fawkes-insights__knoppen">
            <button type="button" className="fui-button fui-button--secondary"
              onClick={() => { setBezigMetBewaren(false); setFout(null); }}>
              {t("common.cancel")}
            </button>
            <button type="button" className="fui-button fui-button--primary"
              disabled={!naam.trim()} onClick={() => void bewaar()}>
              {t("insights.bewaren")}
            </button>
          </div>
        </div>
      )}

      {fout && <p className="fawkes-insights__fout">{fout}</p>}
      </>
      )}
    </section>
  );
}
